import {
  displayAgentUnavailable,
  displayMessage,
  sendEvent,
  sendMessageSentEvent,
  setInputState,
  setLiveChatPromptState
} from "./utils";
import { SELECTORS } from "./constants";

// Should be one of "disconnected", "connecting", "connected", or "unavailable"
export let liveChatState = "disconnected";

// Used to track active WebSocket connection time
let connectionOpenedAt = null;

let webSocket = null;

export function startZendeskChat(wrapper) {
  if (liveChatState !== "disconnected") {
    console.warn("Invalid state for starting chat:", liveChatState);
    return;
  }

	clearMessageHistory(wrapper);
  const messageText = "Connecting to first available consultant... <br /><br />Please don't close or navigate away from this page, as it will close this chat and lose your place in the queue.";
  displayMessage(wrapper, messageText, "info");
  setInputState(wrapper, false);
  setLiveChatPromptState(wrapper, false);
  liveChatState = "connecting";

  connectToWebSocket(wrapper);
  updateCloseButtonText(wrapper, "End chat");
}

export function sendMessageToZendesk(wrapper, message) {
  displayMessage(wrapper, message, "user");
  webSocket.send(message);
  sendMessageSentEvent(wrapper, message, "user");
}

function onWebSocketOpen(wrapper, event) {
  sendEvent(wrapper, "ia_live_chat_websocket_opened");
  connectionOpenedAt = Date.now();
}

// Handles incoming WebSocket messages
// The `type` should be one of the following:
// - 'connected' - indicates that an agent has joined the Zendesk chat
// - 'agentMessage' - contains a message from the agent
// - 'agentLeft' - the agent has left the chat
// - 'agentUnavailable' - no agents are available to take the chat
// - 'error' - an error occurred in the worker
function onWebSocketMessageReceived(wrapper, event) {
  let message;
  try {
    message = JSON.parse(event.data);
  } catch (e) {
    console.error("Failed to parse WebSocket message:", e, event.data);
    return;
  }

  if (!message.type) { return; }

  switch (message.type) {
    case "agentMessage":
      if (!message.content) return;

      liveChatState = "connected";
      setInputState(wrapper, true);
      displayMessage(wrapper, message.content, "live-agent");
      sendMessageSentEvent(wrapper, message.content, "agent");
      break;

    case "agentLeft":
      // TODO: https://simplybusiness.atlassian.net/browse/CMD-2293
      break;

    case "agentUnavailable":
      webSocket.close();
      liveChatState = "disconnected";
      setInputState(wrapper, false);
      displayAgentUnavailable(wrapper);
      sendEvent(wrapper, "ia_live_chat_agent_unavailable");
      break;

    case "error":
      console.error("WebSocket error message:", message.error);
      displayMessage(wrapper, "An error occurred while trying to send your message. Please try again.", "error");
      sendEvent(wrapper, "ia_live_chat_message_error");
      break;
  }
}

function onWebSocketError(wrapper, error) {
  console.error("WebSocket error:", error);
  displayMessage(wrapper, "Something went wrong with the live chat. Please, call us on +44 1604 279528 to speak with someone right away or try again later.", "error");
  liveChatState = "disconnected";

  sendEvent(wrapper, "ia_live_chat_websocket_error", `${error.type}`);
}

function onWebSocketClose(wrapper, event) {
  console.log("WebSocket closed:", event);
  displayMessage(wrapper, "The live chat has ended.", "info");
  liveChatState = "disconnected";
  setInputState(wrapper, false);
  updateCloseButtonText(wrapper, "Close");

  const duration = connectionOpenedAt ? Date.now() - connectionOpenedAt : null;
  sendEvent(wrapper, "ia_live_chat_ended", `code: ${event.code}, reason: ${event.reason}, wasClean: ${event.wasClean}, durationMs: ${duration}`);
  connectionOpenedAt = null;
}

function connectToWebSocket(wrapper) {
  try {
    const element = document.querySelector('.zendesk-data');
    if(!element || !element.dataset || !element.dataset.zendesk) {
      throw new Error("Couldn't find required chat data in the DOM");
    }

    const parsed = JSON.parse(element.dataset.zendesk);
    const url = parsed?.chat_url;
    if (!url) throw new Error("Couldn't find chat endpoint");
    delete parsed.chat_url;

    let connectionId = wrapper.dataset.chatSessionId;
    if (!connectionId) {
      connectionId = `no-product::${crypto.randomUUID()}`;
      wrapper.dataset.chatSessionId = connectionId;
    }
    parsed['connectionId'] = connectionId;

    const chatInput = wrapper.querySelector(SELECTORS.CHAT_INPUT);
    const { insProduct } = chatInput.dataset;
    if (insProduct) {
      parsed['insurance_product'] = insProduct;
    }

    const queryParams = new URLSearchParams(parsed).toString();

    webSocket = new WebSocket(`${url}connect?${queryParams}`);
    webSocket.onopen = (event => onWebSocketOpen(wrapper, event));
    webSocket.onmessage = (event) => onWebSocketMessageReceived(wrapper, event);
    webSocket.onerror = (error) => onWebSocketError(wrapper, error);
    webSocket.onclose = (event) => onWebSocketClose(wrapper, event);
  } catch (e) {
    console.error("Failed to connect to WebSocket", e);
    displayMessage(wrapper, "An error occurred while trying to connect to a consultant. Please, call us on +44 1604 279528 to speak with someone right away or try again later.", "error");
    setInputState(wrapper, false);
    liveChatState = "disconnected";
  }
}

export function updateCloseButtonText(wrapper, endText) {
  const closeButton = wrapper
    .closest(SELECTORS.DIALOG)
    .querySelector(SELECTORS.CLOSE_BUTTON);

  if (closeButton) {
    const svg = closeButton.querySelector('svg');
    const svgClone = svg ? svg.cloneNode(true) : null;

    closeButton.textContent = endText;
    if (svgClone) {
      closeButton.insertBefore(svgClone, closeButton.firstChild);
    }
  }
}

function clearMessageHistory(wrapper) {
	const messages = wrapper.querySelector(SELECTORS.MESSAGES_ELEMENT);
	messages.textContent = '';
}

export function handleDialogClose(event) {
  const wrapper = event.target.querySelector(SELECTORS.CHAT_WRAPPER);

  if (!wrapper) return;
  if (!webSocket) return;

  if (liveChatState === "connected" || liveChatState === "connecting") {
    webSocket.close();
    liveChatState = "disconnected";
    setInputState(wrapper, false);
    updateCloseButtonText(wrapper, "Close");
    sendEvent(wrapper, "ia_dialog_closed");
  }
}
